import { useMemo } from 'react';
import { useMeasure } from 'react-use';
import { Content } from 'src/contexts/ContentContext';
import { Switch } from 'src/core/interim/Switch';
import { PosFormField } from 'src/core/POS/PosFormField';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { Listing } from 'src/WebApiController';

import { GroupsPreview } from '../GroupListingsV2/components/GroupsPreview';
import { EventGroupings } from './components/EventGroupings';
import { GroupListingsBodyProps } from './components/groupingTypes';
import { flattenListingGroup } from './components/groupingUtils';
import { useFullySoldFilter } from './GroupListingsBody.hook';

export const GroupListingsBody = ({
  events,
  listingGroup,
  onEventSelected,
}: GroupListingsBodyProps) => {
  const {
    excludeFullySold,
    showExcludeFullySold,
    sanitizedEvents,
    mergeListingGroupInputs,
    onToggleExcludeFullySold,
  } = useFullySoldFilter(events, listingGroup);

  const [containerRef, { height: containerHeight }] =
    useMeasure<HTMLDivElement>();

  // Listings within the group or not assigned with any group
  const groupListings = useMemo(() => {
    if (listingGroup && mergeListingGroupInputs?.length === 1) {
      return (
        sanitizedEvents
          .flatMap(
            ({ entities }) =>
              entities.listings
                ?.flatMap(flattenListingGroup)
                .map((l) => l as Listing) ?? []
          )
          .filter(
            (listing) =>
              listing?.id > 0 &&
              (listing.ltGrpId === listingGroup.ltGrpId ||
                listing.ltGrpId == null)
          ) ?? []
      );
    }
    return [];
  }, [sanitizedEvents, listingGroup, mergeListingGroupInputs?.length]);

  return (
    <Stack
      direction="column"
      gap={listingGroup ? undefined : 'xl'}
      height="full"
    >
      {showExcludeFullySold && (
        <PosFormField
          label={<Content id={ContentId.ExcludeFullySoldListings} />}
        >
          <Switch
            checked={excludeFullySold}
            onChange={onToggleExcludeFullySold}
          />
        </PosFormField>
      )}
      <Stack
        ref={containerRef}
        direction="column"
        style={{ height: listingGroup ? '100%' : '90%' }}
      >
        {listingGroup ? (
          <GroupsPreview
            eventListings={groupListings}
            groups={mergeListingGroupInputs}
          />
        ) : (
          <EventGroupings
            events={sanitizedEvents}
            onEventSelected={onEventSelected}
            containerHeight={containerHeight}
          />
        )}
      </Stack>
    </Stack>
  );
};
